import React, { Component } from "react";
import { Checkbox, Modal, Progress, Space } from "antd";
import { ModalItem, ButtonItem } from "./Items";

// Arriba de esto el ZIP se nota: el navegador arma todos los PDFs y los
// comprime en memoria. Mismo aviso que usa la descarga de archivos de lotes.
const MAX_SIN_CONFIRMAR = 50;

/**
 * Modal de la descarga masiva de documentos de un listado (cotizaciones,
 * pedidos, embarques y ventas).
 *
 * A diferencia de `ModalFormatoDescarga` (recibos de nómina), aquí los formatos
 * NO son excluyentes: se combinan en un mismo ZIP.
 *
 * `tieneXml` prende la casilla del XML; solo aplica a Ventas, donde el CFDI
 * timbrado viaja junto con los datos del PDF.
 *
 * Con la prop `opciones` (`[{ clave, etiqueta, descripcion, porOmision }]`) se
 * pueden sustituir las casillas por otras; es lo que usa el Reporte de
 * Conciliacion Facturas - Pagos, que baja factura y complementos. Sin ella el
 * modal se comporta exactamente igual que antes.
 */

const OPCIONES_DOCUMENTOS = [
  { clave: "incluirPdf", etiqueta: "PDF de cada documento", porOmision: true },
  {
    clave: "incluirXml",
    etiqueta: "XML de los facturados",
    descripcion: "Las remisiones sin factura no tienen XML y se omiten.",
    porOmision: true,
    soloConXml: true,
  },
  {
    clave: "incluirExcelDocumento",
    etiqueta: "Excel por documento",
    descripcion: "El mismo documento en Excel, uno por archivo.",
    porOmision: false,
  },
  {
    clave: "incluirExcel",
    etiqueta: "Excel de resumen",
    descripcion: "Un solo archivo con las hojas Resumen y Detalle de todo lo seleccionado.",
    porOmision: false,
  },
];

class ModalDescargarDocumentos extends Component {
  constructor(props) {
    super(props);
    const seleccion = {};
    for (const opcion of this.opciones()) seleccion[opcion.clave] = !!opcion.porOmision;
    this.state = seleccion;
  }

  opciones() {
    return this.props.opciones || OPCIONES_DOCUMENTOS;
  }

  /** Una casilla `soloConXml` no cuenta cuando el módulo no tiene XML. */
  disponibles() {
    return this.opciones().filter((opcion) => !opcion.soloConXml || this.props.tieneXml);
  }

  formatos() {
    const formatos = {};
    for (const opcion of this.opciones()) {
      const disponible = !opcion.soloConXml || this.props.tieneXml;
      formatos[opcion.clave] = disponible && !!this.state[opcion.clave];
    }
    return formatos;
  }

  confirmar() {
    const { total, onConfirm } = this.props;
    const formatos = this.formatos();

    if (total > MAX_SIN_CONFIRMAR) {
      Modal.confirm({
        title: `Descargar ${total} documentos`,
        content:
          "El ZIP se arma en tu navegador, así que con esta cantidad puede tardar y consumir bastante memoria. ¿Continuar?",
        okText: "Descargar",
        cancelText: "Cancelar",
        onOk: () => onConfirm(formatos),
      });
      return;
    }
    onConfirm(formatos);
  }

  render() {
    const { visible, onClose, loading, total, progreso, descripcion } = this.props;
    const disponibles = this.disponibles();
    const nadaSeleccionado = !disponibles.some((opcion) => this.state[opcion.clave]);

    return (
      <ModalItem
        title="Descargar seleccionados"
        visible={visible}
        onCancel={loading ? undefined : onClose}
        width={430}
      >
        <p style={{ color: "#888", fontSize: 12, marginBottom: 12 }}>
          {descripcion
            || (total === 1
              ? "Se descargará un ZIP con 1 documento."
              : `Se descargará un ZIP con ${total} documentos.`)}
        </p>

        <Space direction="vertical">
          {disponibles.map((opcion) => (
            <div key={opcion.clave}>
              <Checkbox
                checked={!!this.state[opcion.clave]}
                disabled={loading}
                onChange={(e) => this.setState({ [opcion.clave]: e.target.checked })}
              >
                {opcion.etiqueta}
              </Checkbox>
              {opcion.descripcion && (
                <div style={{ color: "#888", fontSize: 11, marginTop: 2, marginLeft: 24 }}>
                  {opcion.descripcion}
                </div>
              )}
            </div>
          ))}
        </Space>

        {loading && (
          <div style={{ marginTop: 18 }}>
            <Progress
              percent={progreso && progreso.total ? Math.round((progreso.hechos / progreso.total) * 100) : 0}
              status="active"
              strokeColor="#00BFD6"
            />
            <div style={{ color: "#888", fontSize: 11 }}>
              {progreso && progreso.total
                ? `Preparando ${progreso.hechos} de ${progreso.total}...`
                : "Preparando la descarga..."}
              {" "}Puedes esperar aquí, no cierres la pestaña.
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <ButtonItem onClick={onClose} disabled={loading}>Cancelar</ButtonItem>
          <ButtonItem
            type="primary"
            loading={loading}
            disabled={nadaSeleccionado}
            onClick={this.confirmar.bind(this)}
          >
            Descargar
          </ButtonItem>
        </div>
      </ModalItem>
    );
  }
}

export default ModalDescargarDocumentos;
